import React, { useEffect } from 'react';
import { observer } from 'mobx-react';
// From the package root, which jest can load (the deep paths the Users tab imports are ES modules only).
import { DataGrid, GridColDef } from '@mui/x-data-grid';
import { Box, Button, TextField, Typography } from '@mui/material';
import { useSnackbar } from 'notistack';
import moment from 'moment';
import type AdminCompaniesStore from '../../store/adminCompanies/adminCompaniesStore';
import type { TAdminCompany } from '../../services/models/BillingModels';
import { groupOrganisationNumber } from '../../store/purchase/organisationNumber';
import CircularLoading from '../../components/loading/circularLoading';
import NewCompanyDialog from './NewCompanyDialog';
import { companyStatusLabel, companyStatusOf } from './companyStatusLabels';
import { actionsColumn, copyCellOnDoubleClick, noSelectCellStyles } from './adminGrid';

/**
 * The admin panel's Companies tab: every company in a grid like the Users tab's, with the same
 * client-side search and double-click to copy a cell, closed companies greyed. New company opens the
 * New company dialog; Open on a row opens the company page.
 */
const AdminCompaniesTab = observer(function AdminCompaniesTab({ adminCompaniesStore, onOpenCompany }: { adminCompaniesStore: AdminCompaniesStore; onOpenCompany: (id: number) => void }) {
  const { filteredCompanies, isLoadingList, listError, search } = adminCompaniesStore;
  const { enqueueSnackbar } = useSnackbar();

  useEffect(() => {
    void adminCompaniesStore.loadCompanies();
  }, [adminCompaniesStore]);

  const columns: GridColDef<TAdminCompany>[] = [
    { field: 'name', headerName: 'Name', width: 240 },
    { field: 'orgNumber', headerName: 'Organisation number', width: 170, valueFormatter: (params: any) => groupOrganisationNumber(String(params.value)) },
    { field: 'invoiceEmail', headerName: 'Invoice email', width: 240 },
    { field: 'openMemberCount', headerName: 'Members', width: 100, type: 'number' },
    {
      field: 'createdAtUtc',
      headerName: 'Created',
      width: 150,
      valueFormatter: (params: any) => moment(params.value).format('DD.MM.YYYY HH:mm'),
    },
    { field: 'status', headerName: 'Status', width: 100, valueGetter: (params: any) => companyStatusLabel(params.row) },
    actionsColumn<TAdminCompany>({
      width: 110,
      sortable: false,
      renderCell: (params) => (
        <Button variant='outlined' onClick={() => onOpenCompany(params.row.id)} style={{ padding: '6px 12px', cursor: 'pointer' }}>
          Open
        </Button>
      ),
    }),
  ];

  if (isLoadingList && filteredCompanies.length === 0) {
    return <CircularLoading loadingText='Loading all companies...' />;
  }

  return (
    <div style={{ flex: '1 1 auto', display: 'grid', placeContent: 'center' }}>
      <Box sx={{ height: 800, width: '1200px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'end', gap: 16 }}>
          <div style={{ display: 'flex', alignItems: 'end', gap: 16 }}>
            <Button variant='contained' onClick={adminCompaniesStore.openNewCompany} sx={{ marginBottom: 2 }}>
              New company
            </Button>
            <Typography variant='subtitle2' color='text.secondary' sx={{ marginBottom: 2 }}>
              Double click a cell to copy its content.
            </Typography>
          </div>
          <TextField label='Search...' variant='standard' value={search} onChange={(e) => adminCompaniesStore.setSearch(e.target.value)} sx={{ marginBottom: 2 }} />
        </div>
        {listError !== null && (
          <Typography variant='body2' color='error' role='alert' sx={{ marginBottom: 2 }}>
            {listError}
          </Typography>
        )}
        <DataGrid
          onCellDoubleClick={copyCellOnDoubleClick((message) => enqueueSnackbar(message, { variant: 'info' }))}
          disableColumnSelector
          disableSelectionOnClick
          // Tens of companies: every row is rendered, so none is missing from a page or a test.
          disableVirtualization
          checkboxSelection={false}
          rows={filteredCompanies}
          columns={columns}
          getRowClassName={(params) => (companyStatusOf(params.row) === 'closed' ? 'company-closed' : '')}
          sx={{
            '& .company-closed': {
              color: 'text.disabled',
            },
            ...noSelectCellStyles,
          }}
        />
      </Box>
      <NewCompanyDialog adminCompaniesStore={adminCompaniesStore} />
    </div>
  );
});

export default AdminCompaniesTab;
